"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  REPUBLISHED_BOOK_FIELDS as F,
  formatRepublishedBook,
  type RepublishedBookLabels,
  type RepublishedEdition,
  republishedBookIsReady,
} from "@/lib/tool-kit/special-sources/republished-book-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const REPUBLISHED_BOOK_CITATION_GENERATOR_STRINGS = {
  formHeading: "Book details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the book's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  editionLabel: "Which edition did you read?",
  editionPrint: "A print or ebook edition",
  editionOnline: "A free online edition (Project Gutenberg and the like)",
  authorsLabel: "Authors, one per line",
  authorsPlaceholder: "Jane Austen",
  authorsHint:
    "First name then surname. For a two-part surname, type the surname first with a comma: García Márquez, Gabriel.",
  titleLabel: "Title",
  titlePlaceholder: "Pride and prejudice",
  titleHint:
    "Type it in sentence case — only the first word and proper nouns capitalized. The tool capitalizes it for MLA and Chicago.",
  originalYearLabel: "Year first published",
  originalYearPlaceholder: "1813",
  originalYearHint:
    "From the copyright page or a library catalogue. Leave it blank if you cannot confirm it.",
  originalPublisherLabel: "First publisher (optional)",
  originalPublisherPlaceholder: "T. Egerton",
  originalPublisherHint:
    "Used by Chicago and by MLA for online editions. For a book first published before 1900 you may add the city: London: T. Egerton.",
  translatorsLabel: "Translators, one per line (optional)",
  editorsLabel: "Editors of this edition, one per line (optional)",
  publisherLabel: "Publisher of your edition",
  publisherPlaceholder: "Penguin Books",
  yearLabel: "Year of your edition",
  yearPlaceholder: "2003",
  siteLabel: "Website",
  sitePlaceholder: "Project Gutenberg",
  postedLabel: "Date the site released it",
  postedPlaceholder: "YYYY-MM-DD",
  postedHint: "Project Gutenberg shows it as the Release Date.",
  urlLabel: "URL",
  urlPrintLabel: "URL (optional, for an ebook with a stable link)",
  urlPlaceholder: "https://www.gutenberg.org/ebooks/1342",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation (add the page number)",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  bibliographyFirstPublished: "Bibliography — “First published” form",
  apaNote:
    "APA dates the reference by the edition you read and closes it with the original year; both years appear in the text, earlier first.",
  mlaNote:
    "MLA lets you add the original year after the title. For an online edition, the original publication comes first and the website after it.",
  chicagoNote:
    "Chicago marks a reprint with repr. in the note and Reprint in the bibliography, or adds First published at the end. Use one form throughout your paper.",
  examplePrint: {
    authors: "Jane Austen",
    title: "Pride and prejudice",
    originalYear: "1813",
    originalPublisher: "T. Egerton",
    publisher: "Penguin Books",
    year: "2003",
  },
  exampleOnline: {
    authors: "Jane Austen",
    title: "Pride and prejudice",
    originalYear: "1813",
    originalPublisher: "T. Egerton",
    site: "Project Gutenberg",
    posted: "1998-06-01",
    url: "https://www.gutenberg.org/ebooks/1342",
  },
} as const;

type Strings = typeof REPUBLISHED_BOOK_CITATION_GENERATOR_STRINGS;

function editionFields(
  strings: Strings,
  edition: RepublishedEdition
): SourceField[] {
  if (edition === "online") {
    return [
      {
        id: F.site,
        kind: "text",
        label: strings.siteLabel,
        placeholder: strings.sitePlaceholder,
      },
      {
        id: F.posted,
        kind: "date",
        label: strings.postedLabel,
        placeholder: strings.postedPlaceholder,
        hint: strings.postedHint,
      },
      {
        id: F.url,
        kind: "url",
        label: strings.urlLabel,
        placeholder: strings.urlPlaceholder,
        wide: true,
      },
    ];
  }
  return [
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlPrintLabel,
      wide: true,
    },
  ];
}

function buildFields(
  strings: Strings,
  edition: RepublishedEdition
): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.originalYear,
      kind: "text",
      label: strings.originalYearLabel,
      placeholder: strings.originalYearPlaceholder,
      hint: strings.originalYearHint,
    },
    {
      id: F.originalPublisher,
      kind: "text",
      label: strings.originalPublisherLabel,
      placeholder: strings.originalPublisherPlaceholder,
      hint: strings.originalPublisherHint,
    },
    ...editionFields(strings, edition),
    { id: F.translators, kind: "people", label: strings.translatorsLabel },
    { id: F.editors, kind: "people", label: strings.editorsLabel },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function republishedBookLabels(strings: Strings): RepublishedBookLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    bibliographyFirstPublished: strings.bibliographyFirstPublished,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function RepublishedBookCitationGeneratorWidget() {
  const strings = useToolUi(REPUBLISHED_BOOK_CITATION_GENERATOR_STRINGS);
  const [edition, setEdition] = useState<RepublishedEdition>("print");
  const fields = useMemo(
    () => buildFields(strings, edition),
    [strings, edition]
  );
  const labels = useMemo(() => republishedBookLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatRepublishedBook(values, edition, labels),
    [edition, labels]
  );

  return (
    <SourceCitationBuilder
      example={
        edition === "online" ? strings.exampleOnline : strings.examplePrint
      }
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.editionLabel}
          name="republished-book-edition"
          onChange={setEdition}
          options={[
            { value: "print", label: strings.editionPrint },
            { value: "online", label: strings.editionOnline },
          ]}
          value={edition}
        />
      }
      isReady={republishedBookIsReady}
      strings={strings}
    />
  );
}
